import React from 'react';
import { Link } from 'react-router-dom';
import { Package, ShoppingCart, Plus } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useToast } from '../hooks/use-toast';
import { resolveImageUrl } from './ImageUploader';
import { usePricing } from '../lib/pricing';

const ProductCard = ({ product }) => {
  const { addItem } = useCart();
  const { toast } = useToast();
  const priceOf = usePricing();
  const price = priceOf(product);

  const handleAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addItem(product, 1);
    toast({ title: 'Aggiunto al carrello', description: product.name });
  };

  return (
    <Link
      to={`/prodotto/${product.slug}`}
      className="group bg-white border border-neutral-200 rounded-lg overflow-hidden hover:shadow-xl hover:border-[#d62828]/40 transition-all duration-300 flex flex-col"
    >
      <div className="aspect-square bg-neutral-50 overflow-hidden flex items-center justify-center relative">
        {product.image_url ? (
          <img
            src={resolveImageUrl(product.image_url)}
            alt={product.name}
            className="max-w-full max-h-full w-auto h-auto object-contain group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        ) : (
          <Package size={64} className="text-neutral-300" />
        )}
        {price.hasDiscount && (
          <span
            data-testid="product-card-discount-badge"
            className="absolute top-2 left-2 bg-[#d62828] text-white text-xs font-black px-2 py-1 rounded shadow-md"
          >
            -{Math.round(price.percent)}%
          </span>
        )}
        <button
          onClick={handleAdd}
          aria-label="Aggiungi al carrello"
          className="absolute top-2 right-2 w-9 h-9 rounded-full bg-white/90 backdrop-blur border border-neutral-200 flex items-center justify-center text-neutral-700 hover:bg-[#d62828] hover:text-white hover:border-[#d62828] transition-all opacity-0 group-hover:opacity-100"
        >
          <Plus size={16} />
        </button>
      </div>
      <div className="p-4 flex-1 flex flex-col">
        {product.brand && (
          <span className="text-xs uppercase tracking-wider text-neutral-500 font-medium">
            {product.brand}
          </span>
        )}
        <h3 className="font-bold text-neutral-800 mt-1 line-clamp-2 group-hover:text-[#d62828] transition-colors">
          {product.name}
        </h3>
        {product.short_description && (
          <p className="text-sm text-neutral-600 mt-2 line-clamp-2">{product.short_description}</p>
        )}
        <div className="flex items-end justify-between mt-auto pt-3 gap-2">
          {product.price_per_day > 0 ? (
            <div className="flex flex-col leading-tight" data-testid="product-card-price">
              {price.hasDiscount ? (
                <>
                  <span className="text-xs text-neutral-400 line-through">€ {price.original.toFixed(2)}/g</span>
                  <span className="text-sm font-bold text-[#d62828]">
                    € {price.discounted.toFixed(2)}<span className="text-xs font-normal text-neutral-500">/giorno</span>
                  </span>
                  <span className="text-[11px] text-neutral-600 mt-0.5">
                    Weekend <span className="line-through text-neutral-400">€ {price.weekendOriginal.toFixed(2)}</span>
                    {' '}<strong className="text-[#d62828]">€ {price.weekendDiscounted.toFixed(2)}</strong>
                  </span>
                </>
              ) : (
                <>
                  <span className="text-sm font-bold text-neutral-800">
                    € {price.original.toFixed(2)}<span className="text-xs font-normal text-neutral-500">/giorno</span>
                  </span>
                  <span className="text-[11px] text-neutral-500 mt-0.5">
                    Weekend <strong className="text-neutral-700">€ {price.weekendOriginal.toFixed(2)}</strong>
                  </span>
                </>
              )}
            </div>
          ) : (
            <span className="text-xs text-neutral-500">Su richiesta</span>
          )}
          <button
            onClick={handleAdd}
            className="text-xs font-bold text-[#d62828] hover:text-white hover:bg-[#d62828] px-2 py-1 rounded border border-[#d62828] transition-colors flex items-center gap-1 flex-shrink-0"
          >
            <ShoppingCart size={12} /> Aggiungi
          </button>
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;
